import { useEffect, useMemo, type ReactNode } from 'react';
import { DownloadMenu } from '@/components/ui/DownloadMenu';
import type { ColumnDefinition } from '@/lib/export';
import type { Ledger, LedgerType } from '@/api/types';
import { formatDateISO } from '@/utils/date';

/**
 * Build column definitions for ledger export.
 */
function getLedgerExportColumns(): ColumnDefinition[] {
    return [
        { key: 'label', label: 'Name' },
        { key: 'mwrr', label: 'MWRR', format: 'percent' },
        { key: 'profitLoss', label: 'P&L', format: 'money' },
        { key: 'gainPercentage', label: 'P&L %', format: 'percent' },
        { key: 'value', label: 'Value', format: 'money' },
    ];
}

/**
 * Transform ledger data for export.
 */
function transformLedgersForExport(ledgers: Ledger[]): unknown[] {
    return ledgers.map((ledger) => ({
        label: ledger.label,
        mwrr: ledger.analytics?.mwrr ?? null,
        profitLoss: ledger.analytics?.profitLoss ?? null,
        gainPercentage: ledger.analytics?.gainPercentage ?? null,
        value: ledger.latestValue?.convertedValue ?? null,
    }));
}

interface UseLedgerExportOptions {
    ledgers: Ledger[];
    type: LedgerType;
    onHeaderAction?: (node: ReactNode) => void;
}

/**
 * Hook to handle ledger export functionality.
 * Sets up a DownloadMenu in the header via onHeaderAction callback.
 */
export function useLedgerExport({ ledgers, type, onHeaderAction }: UseLedgerExportOptions): void {
    const exportData = useMemo(() => transformLedgersForExport(ledgers), [ledgers]);
    const exportColumns = useMemo(() => getLedgerExportColumns(), []);
    const exportFilename = `${type}-ledgers-${formatDateISO()}`;

    useEffect(() => {
        if (onHeaderAction) {
            onHeaderAction(
                <DownloadMenu
                    data={exportData}
                    columns={exportColumns}
                    filename={exportFilename}
                />
            );
        }
        return () => {
            if (onHeaderAction) {
                onHeaderAction(null);
            }
        };
    }, [onHeaderAction, exportData, exportColumns, exportFilename]);
}
